import { useState } from 'react';
import { siteConfig, testimonials, faqData } from '@/mocks/site';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';


const aboutImage = 'https://readdy.ai/api/search-image?query=A%20modern%20editorial%20newsroom%20with%20warm%20lighting%2C%20desks%20with%20computer%20screens%2C%20journalism%20atmosphere%2C%20professional%20yet%20creative%20workspace%2C%20warm%20golden%20and%20deep%20red%20accents%2C%20no%20people%2C%20sophisticated%20media%20company%20interior&width=1200&height=600&seq=about-hero-01&orientation=landscape';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qs26ue3oq4jqumtptg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(errMsg);
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormState('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    foundingDate: siteConfig.foundingYear,
    email: siteConfig.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
      streetAddress: siteConfig.address,
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteConfig.phone,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    sameAs: [
      siteConfig.socialLinks.weibo,
      siteConfig.socialLinks.douyin,
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Breadcrumb */}
          <div className="px-4 md:px-6 py-6">
            <div className="max-w-6xl mx-auto">
              <Breadcrumb items={[{ label: '关于我们' }]} />
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="px-4 md:px-6 pb-12">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 items-center">
                <div className="flex-1">
                  <span className="inline-block px-3 py-1 mb-4 text-xs font-semibold text-primary-500 bg-primary-50 rounded-full">
                    关于 {siteConfig.name}
                  </span>
                  <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                    娱乐圈内幕的<span className="text-primary-500">守夜人</span>
                  </h1>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    {siteConfig.name}成立于{siteConfig.foundingYear}年，由一群深耕娱乐圈多年的资深媒体人和记者联合创立。我们致力于打破娱乐圈的信息壁垒，为公众提供第一手、最真实的娱乐圈资讯。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    不做标题党，不传假新闻，不向公关低头——这是我们的三大原则。我们的编辑团队拥有遍布全国的线人网络，每一条爆料都经过多方交叉验证，确保信息的真实性和可靠性。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    无论你是普通吃瓜群众，还是业内同行，{siteConfig.name}都欢迎你的关注和参与。让我们一起，揭开娱乐圈最真实的一面。
                  </p>
                </div>
                <div className="flex-1">
                  <img
                    src={aboutImage}
                    alt="黑料不打烊编辑部"
                    className="rounded-lg w-full h-64 md:h-80 object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: '24H', label: '全天候更新' },
                  { value: '500+', label: '线人网络' },
                  { value: '1000万+', label: '月活跃读者' },
                  { value: '99.7%', label: '信息准确率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 mb-3 text-xs font-semibold text-accent-700 bg-accent-50 rounded-full">
                  读者口碑
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  他们都在看
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {testimonials.map((t, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-background-200/70 bg-background-50 p-5 flex flex-col gap-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                        <img
                          src={t.avatar}
                          alt={t.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.role}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <span className="inline-block px-3 py-1 mb-3 text-xs font-semibold text-primary-700 bg-primary-50 rounded-full">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  欢迎爆料
                </h2>
                <p className="text-sm text-foreground-600">
                  如果你有娱乐圈的最新内幕或独家线索，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <span className="text-xs text-foreground-500">邮箱</span>
                  <a href={`mailto:${siteConfig.email}`} className="text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                    {siteConfig.email}
                  </a>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <span className="text-xs text-foreground-500">电话</span>
                  <span className="text-sm font-medium text-foreground-900">{siteConfig.phone}</span>
                </div>
                <div className="flex flex-col items-center gap-2 p-4 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <span className="text-xs text-foreground-500">地址</span>
                  <span className="text-sm font-medium text-foreground-900 text-center">{siteConfig.address}</span>
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="rounded-lg border border-background-200/70 bg-background-50 p-6 flex flex-col gap-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="about-name" className="text-sm font-medium text-foreground-700">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="about-name"
                      name="name"
                      type="text"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="你的称呼"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="about-email" className="text-sm font-medium text-foreground-700">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="about-email"
                      name="email"
                      type="email"
                      required
                      className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="about-subject" className="text-sm font-medium text-foreground-700">
                    主题
                  </label>
                  <input
                    id="about-subject"
                    name="subject"
                    type="text"
                    className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="爆料主题"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="about-message" className="text-sm font-medium text-foreground-700">
                    内容 <span className="text-primary-500">*</span>
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                    placeholder="请详细描述你要爆料的内容（最多500字）"
                  ></textarea>
                  <span className="text-xs text-foreground-400">最多500个字符</span>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="bot-field"
                />

                <button
                  type="submit"
                  disabled={formState === 'submitting'}
                  className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 text-sm whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {formState === 'submitting' ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i> 提交中...
                    </span>
                  ) : (
                    '提交爆料'
                  )}
                </button>

                {formState === 'success' && (
                  <div className="p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                    <i className="ri-checkbox-circle-line mr-1.5"></i>
                    感谢你的爆料！我们会尽快核实并处理。
                  </div>
                )}
                {formState === 'error' && formError && (
                  <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                    <i className="ri-error-warning-line mr-1.5"></i>
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </section>

          {/* FAQ */}
          <section className="px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <span className="inline-block px-3 py-1 mb-3 text-xs font-semibold text-primary-700 bg-primary-50 rounded-full">
                  常见问题
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  你可能有这些疑问
                </h2>
              </div>

              <div className="flex flex-col gap-3">
                {faqData.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden"
                  >
                    <button
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    >
                      <span className="font-medium text-foreground-900 text-sm">{faq.question}</span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-300 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === index ? 'max-h-96 pb-4 px-5' : 'max-h-0'
                      }`}
                    >
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}